import { matchPath } from 'react-router-dom';
import { OUTBOX_SEGMENT } from '@/features/outbox/route';
import { SCHEDULED_SEGMENT, SNOOZED_SEGMENT } from '@/features/scheduling/route';
import { LABEL_SEGMENT, UNIFIED_SEGMENT } from '@/routes/mail/unifiedRoute';

/**
 * The route TEMPLATE an error happened on, never the path itself.
 *
 * A real path carries folder, message, account and contact ids; the template
 * says which view broke, which is all a report needs. Mirrors the routes in
 * `app/router.tsx` (a test walks `shellChildRoutes` to keep the two in step).
 *
 * Order matters: the first match wins, so every static route sits above the
 * parameterised route it would otherwise lose to (`/mail/outbox` before
 * `/mail/:folderId`, `/admin` before `/admin/:tab`).
 */
export const ROUTE_TEMPLATES: readonly string[] = [
  '/',
  '/login',
  '/mailto',
  '/message/:accountId/:messageId',
  '/compose/:accountId',

  '/mail',
  `/mail/${OUTBOX_SEGMENT}`,
  `/mail/${SCHEDULED_SEGMENT}`,
  `/mail/${SNOOZED_SEGMENT}`,
  `/mail/${UNIFIED_SEGMENT}`,
  `/mail/${UNIFIED_SEGMENT}/:accountId/:messageId`,
  `/mail/${LABEL_SEGMENT}/:labelAccountId/:labelId`,
  `/mail/${LABEL_SEGMENT}/:labelAccountId/:labelId/:messageId`,
  '/mail/:folderId',
  '/mail/:folderId/:messageId',

  '/calendar',
  '/calendar/:view',

  '/contacts',
  '/contacts/:contactId',

  '/tasks',
  '/tasks/:listId',
  '/tasks/:listId/:taskId',

  '/settings',
  '/settings/general',
  '/settings/accounts',
  '/settings/filters',
  '/settings/out-of-office',
  '/settings/notifications',
  '/settings/privacy',
  '/settings/ai',
  '/settings/nextcloud',
  '/settings/sessions',

  '/admin',
  '/admin/:tab',
];

/** What an unknown path reports as: the catch-all, never the path. */
export const UNKNOWN_ROUTE = '/*';

/** Map a real pathname to the router template it matches, or `/*`. */
export function routeTemplate(pathname: string): string {
  for (const template of ROUTE_TEMPLATES) {
    try {
      if (matchPath({ path: template, end: true }, pathname)) return template;
    } catch (_error) {
      // A malformed path (bad percent-encoding) matches nothing.
    }
  }
  return UNKNOWN_ROUTE;
}
